import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Collapse, IconButton, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from "@mui/material";
import { FlowConsignadoItemDTO } from "pages/consignado/detail/consignado-detail.model";
import { useState } from "react";
import { ColumnData, currencyOf, deepCopy, resolveWrapper } from "utils/util";
import { FlowConsignadoVisualizarPedidoGeradoDTO, FlowConsignadoVisualizarPedidosDTO } from "../consignado-visualizar-dialog.model";

type FooterInfoMap = {
    [uuidPedido: string]: FooterInfo;
};
type FooterInfo = {
    quantidade: number,
    quantidadeVendida: number,
    quantidadeCancelada: number,
    valorTotalLiquido: number,
};

export const AbaPedido = (props: { data: FlowConsignadoVisualizarPedidosDTO; }) => {

    const [localState, setLocalState] = useState<{ selectedItens: { [uuidPedido: string]: boolean; }; }>({ selectedItens: {} });

    const _columnsItens: ColumnData<FlowConsignadoVisualizarPedidoGeradoDTO & { __toggle: unknown; }>[] = [
        {
            header: '',
            id: 'toggle',
            key: '__toggle',
            width: 10,
            convert: item => <IconButton
                size="small"
                onClick={() => setLocalState(prev => {
                    const key = item.uuid;
                    const { selectedItens } = deepCopy(prev);
                    if (!selectedItens[key]) {
                        selectedItens[key] = true;
                    } else {
                        delete selectedItens[key];
                    }
                    return {
                        ...prev,
                        selectedItens,
                    };
                })}
            >
                {!!localState.selectedItens[item.uuid] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
        },

        {
            header: 'Pedido',
            id: 'pedido',
            key: 'numeroPedido',
            width: 30,
        },

        {
            header: 'Entrega',
            id: 'entrega',
            key: 'numeroEntrega',
            textAlign: 'center',
            width: 6.36,
        },

        {
            header: 'Emissão',
            id: 'emissao',
            key: 'emissao',
            textAlign: 'center',
            width: 6.36,
        },

        {
            header: 'Status',
            id: 'status',
            key: 'status',
            textAlign: 'center',
            width: 6.36,
        },

        {
            header: 'Tipo',
            id: 'tipo',
            key: 'tipo',
            textAlign: 'center',
            width: 6.36,
            convert: item => <>{item.tipo === 'BALCÃO' ? 'FLOW' : item.tipo}</>,
        },

        {
            header: 'Valor total',
            id: 'valor-total',
            key: 'valorLiquido',
            textAlign: 'center',
            width: 6.36,
            convert: item => {
                return <>{currencyOf(item.valorLiquido)}</>;
            },
        },
    ];

    const subColumnsItens: ColumnData<FlowConsignadoItemDTO>[] = [
        {
            header: 'SKU',
            id: 'sku',
            key: 'sku.descritivo',
            width: 60,
        },

        {
            header: 'Quantidade',
            id: 'quantidade',
            key: 'quantidade',
            textAlign: 'center',
            width: 10,
        },

        // {
        //     header: 'Quantidade devolvida',
        //     id: 'quantidade-devolvida',
        //     key: 'quantidadeDevolvida',
        //     textAlign: 'center',
        //     width: 10,
        // },

        {
            header: 'Quantidade vendida',
            id: 'quantidade-vendida',
            key: 'quantidadeVendida',
            textAlign: 'center',
            width: 10,
        },

        {
            header: 'Quantidade cancelada',
            id: 'quantidade-vendida',
            key: 'quantidadeCancelada',
            textAlign: 'center',
            width: 10,
        },

        {
            header: 'Valor unitário',
            id: 'valor-unitario',
            key: 'valorUnitario',
            textAlign: 'center',
            width: 10,
            convert: (item) => {
                if (item.valorUnitario === undefined) return undefined;
                return <>{currencyOf(item.valorUnitario)}</>;
            }
        },

        {
            header: 'Valor total',
            id: 'valor-total',
            key: 'valorTotalLiquido',
            textAlign: 'center',
            width: 10,
            convert: (item) => {
                return <>{currencyOf(item.valorTotalLiquido)}</>;
            }
        },
    ];

    const footer = props.data.pedidos.reduce((acc, curr) => {
        acc[curr.uuid] = curr.itens.reduce((accItem, currItem) => {
            Object.keys(accItem).forEach(key => {
                accItem[key as keyof FooterInfo] += (currItem as any)[key];
            });
            return accItem;
        }, {
            quantidade: 0,
            quantidadeVendida: 0,
            quantidadeCancelada: 0,
            valorTotalLiquido: 0,
        } as FooterInfo);
        return acc;
    }, {} as FooterInfoMap);

    return <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
        }}
    >
        <Table size='small'>
            <TableHead>
                <TableRow>
                    {_columnsItens.map(c => <TableCell key={c.id} width={`${c.width}%`}>
                        <Typography align={c.textAlign} fontWeight={'bold'}>
                            {c.header}
                        </Typography>
                    </TableCell>
                    )}
                </TableRow>
            </TableHead>

            <TableBody>
                {props.data.pedidos.map((item, index) => [
                    <TableRow key={'headerRow-' + item.uuid + index}>
                        {_columnsItens.map(c => <TableCell key={c.id + item.uuid + index}>
                            <Typography align={c.textAlign}>
                                {c.convert?.(item as any, { column: c }) ?? resolveWrapper(item, c.key)}
                            </Typography>
                        </TableCell>
                        )}
                    </TableRow>,

                    <TableRow key={'contentRow-' + item.uuid + index} sx={{ '& > *': { borderBottom: 'unset' } }}>
                        <TableCell style={{ padding: 0 }} colSpan={999}>
                            <Collapse in={!!localState.selectedItens[item.uuid]} timeout="auto" unmountOnExit>

                                <Table size='small'>
                                    <TableHead>
                                        <TableRow>
                                            {subColumnsItens.map((c, cIndex) => <TableCell key={`headerColumn-${c.id}/${item.uuid}/${cIndex}`}>
                                                <Typography align={c.textAlign} fontWeight={'bold'}>
                                                    {c.header}
                                                </Typography>
                                            </TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {item.itens.map((subItem, index) => {
                                            return <TableRow key={item.uuid + subItem.sku.uuid + index}>
                                                {subColumnsItens.map((c, cIndex) => <TableCell key={`rowColumn-${c.id}/${item.uuid}/${subItem.sku.uuid}/${cIndex}`}>
                                                    <Typography align={c.textAlign}>
                                                        {c.convert?.(subItem as any, { column: c }) ?? resolveWrapper(subItem, c.key)}
                                                    </Typography>
                                                </TableCell>
                                                )}
                                            </TableRow>;
                                        })}
                                    </TableBody>

                                    <TableFooter>
                                        <TableRow>
                                            {subColumnsItens.map((c, cIndex) => {
                                                const value = footer[item.uuid];
                                                const itensLength = item.itens.length;
                                                return <TableCell key={'footer-' + c.id + item.uuid + '-' + cIndex}>
                                                    <Typography align={c.textAlign} fontWeight='bold'>

                                                        {cIndex === 0 ? `${itensLength} ${itensLength === 1 ? 'item' : 'itens'}` : c.convert?.(value as any, { column: c }) ?? resolveWrapper(value, c.key)}
                                                    </Typography>
                                                </TableCell>;
                                            })}
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </Collapse>
                        </TableCell>
                    </TableRow>
                ])}
            </TableBody>

            {/* <TableFooter>
                <TableRow >
                    <TableCell colSpan={999}>
                        <Typography fontWeight={'bold'} textAlign={'end'}>
                            {props.data.quantidadePedidos}
                        </Typography>
                    </TableCell>
                </TableRow>
            </TableFooter> */}
        </Table>
    </Box>;
};
